// askSmart-ui/src/pages/MapleCoin/manage/ManageMapleCoin.tsx
//
// Manage MapleCoin (/manage-maplecoin): staff customer servicing. Deliberately
// read-only and search-first — there is no list of established customers,
// no institution-wide figures, and no money-moving or modifying action.
// Every customer, account-activity and transaction view is recorded by the
// server in the access audit.
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, ShieldCheck, ShieldAlert, ShieldQuestion, Users, ChevronRight, Landmark } from 'lucide-react';
import { useAuthentication } from '../../../context/AuthContext';
import { formatMoney, formatSignedAmount, isZero } from '../money';
import { describe as describeTransaction, STATUS_LABEL } from '../components/ActivityList';
import {
  AdminAccount, AdminApiError, AdminControl, AdminTransaction, CustomerRecord, KycSummary, SearchResponse, TransactionDetail,
  getActivity, getCustomer, getTransaction, searchCustomers,
} from './adminApi';
import '../MapleCoin.css';

const PAGE_SIZE = 20;

type View =
  | { kind: 'search' }
  | { kind: 'customer'; customerNumber: string }
  | { kind: 'transaction'; reference: string; customerNumber: string };

const dateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-CA', { dateStyle: 'long', timeStyle: 'short' }) : '—';
const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-CA', { dateStyle: 'long' }) : '—');
const providerName = (p: string | null) => (p ? (p.length <= 4 ? p.toUpperCase() : p[0].toUpperCase() + p.slice(1)) : '—');
const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

function Unavailable({ onRetry }: { onRetry?: () => void }) {
  return (
    <section className="mc-card mc-state" role="alert">
      <span className="mc-empty-icon"><Landmark size={22} /></span>
      <h2 className="mc-state-title">MapleCoin servicing is temporarily unavailable</h2>
      <p>Please try again in a moment.</p>
      {onRetry && <button type="button" className="mc-link mc-state-action" onClick={onRetry}>Try again</button>}
    </section>
  );
}

// ── Search ──────────────────────────────────────────────────────────────────

interface LastSearch {
  q: string;
  result: SearchResponse | null;
}

function SearchView({ onOpen, last, onSearched }: {
  onOpen: (customerNumber: string) => void;
  last: LastSearch;
  onSearched: (s: LastSearch) => void;
}) {
  const [q, setQ] = useState(last.q);
  const [result, setResult] = useState<SearchResponse | null>(last.result);
  const [state, setState] = useState<'idle' | 'loading' | 'invalid' | 'error' | 'forbidden'>('idle');
  const { roles } = useAuthentication();
  const canReviewUsers = roles?.permissions?.includes('users.manage');

  const run = async (e?: FormEvent) => {
    e?.preventDefault();
    const term = q.trim();
    if (!term) {
      setState('invalid');
      setResult(null);
      return;
    }
    setState('loading');
    try {
      const found = await searchCustomers(term);
      setResult(found);
      onSearched({ q: term, result: found });
      setState('idle');
    } catch (err) {
      setResult(null);
      setState(err instanceof AdminApiError && err.status === 400 ? 'invalid'
        : err instanceof AdminApiError && err.status === 403 ? 'forbidden' : 'error');
    }
  };

  return (
    <>
      <h1 className="mc-page-title">Customer servicing</h1>
      <form className="mc-card mc-admin-search" role="search" onSubmit={run}>
        <label htmlFor="mc-admin-q" className="mc-admin-label">Find an established MapleCoin customer</label>
        <div className="mc-admin-search-row">
          <input
            id="mc-admin-q"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Customer number, account number or name"
            autoComplete="off"
          />
          <button type="submit" className="mc-admin-search-btn" disabled={state === 'loading'}>
            <Search size={16} /> {state === 'loading' ? 'Searching…' : 'Search'}
          </button>
        </div>
        <p className="mc-admin-hint">Customer number (C…), account number (10 digits), or at least 3 characters of a name.</p>
      </form>

      {state === 'invalid' && (
        <p className="mc-admin-note" role="alert">
          Enter a customer number, an account number, or at least 3 characters of a name.
        </p>
      )}
      {state === 'error' && <Unavailable onRetry={() => run()} />}
      {state === 'forbidden' && (
        <p className="mc-admin-note" role="alert">
          Customer servicing is available only while signed in to the MapleCoin organization.
        </p>
      )}

      {state === 'idle' && result && (
        <section className="mc-section" aria-label="Search results">
          {result.results.length === 0 ? (
            <div className="mc-card mc-empty"><div className="mc-empty-title">No matching customers</div></div>
          ) : (
            <>
              {result.truncated && (
                <p className="mc-admin-note">Showing the first {result.results.length} matches — refine your search to narrow them.</p>
              )}
              <ul className="mc-admin-results">
                {result.results.map((c) => (
                  <li key={c.customerNumber}>
                    <button type="button" className="mc-account mc-admin-result" onClick={() => onOpen(c.customerNumber)}>
                      <div>
                        <div className="mc-account-code">{c.displayName}</div>
                        <div className="mc-account-name">
                          {c.legalName} · {titleCase(c.customerType)} · {c.customerNumber} · {titleCase(c.status)}
                        </div>
                        <div className="mc-account-name">
                          {c.accounts.length === 0
                            ? 'No account'
                            : c.accounts.map((a) => `${a.currency} ${a.accountNumber} (${titleCase(a.status)})`).join(' · ')}
                        </div>
                      </div>
                      <span className="mc-account-link">Open <ChevronRight size={16} /></span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {state === 'idle' && !result && <p className="mc-admin-hint">Search for a customer to begin.</p>}

      {canReviewUsers && (
        <Link to="/admin/users" className="mc-card mc-admin-users">
          <Users size={18} /> Review signed-up users <ChevronRight size={16} />
        </Link>
      )}
    </>
  );
}

// ── Holds and restrictions (view-only) ──────────────────────────────────────
// Operations places and releases these; Admin only sees them, with their
// history, so a customer's question about unavailable funds can be answered.

const REASON_LABEL: Record<AdminControl['reasonCode'], string> = {
  FRAUD_REVIEW: 'Fraud review',
  COMPLIANCE_REVIEW: 'Compliance review',
  OPERATIONAL: 'Operational',
};
const RESTRICTION_LABEL: Record<NonNullable<AdminControl['restrictionType']>, string> = {
  NO_OUTGOING: 'No outgoing transactions',
  NO_INCOMING: 'No incoming transactions',
};

function holdEnded(h: AdminControl): string {
  if (h.releasedAt) return dateTime(h.releasedAt);
  if (h.status === 'EXPIRED') return dateTime(h.expiresAt ?? null);
  return h.expiresAt ? `Expires ${dateTime(h.expiresAt)}` : '—';
}

function ControlsPanel({ account }: { account: AdminAccount }) {
  const holds = account.holds ?? [];
  const restrictions = account.restrictions ?? [];
  const activeHolds = holds.filter((h) => h.status === 'ACTIVE').length;
  const activeRestrictions = restrictions.filter((r) => r.status === 'ACTIVE').length;
  const plural = (n: number, word: string) => `${n} active ${word}${n === 1 ? '' : 's'}`;
  return (
    <section className="mc-card mc-admin-panel" aria-label={`Holds and restrictions ${account.accountNumber}`}>
      <h2 className="mc-section-title">Holds and restrictions</h2>
      <p className="mc-admin-hint">
        {activeHolds === 0 && activeRestrictions === 0
          ? 'None active.'
          : `${plural(activeHolds, 'hold')}, ${plural(activeRestrictions, 'restriction')}.`}
        {' '}Placed and released by MapleCoin Operations.
      </p>
      {(holds.length > 0 || restrictions.length > 0) && (
        <table className="mc-admin-table">
          <thead>
            <tr><th>Reference</th><th>Kind</th><th>Amount</th><th>Reason</th><th>Status</th><th>Placed</th><th>Ended</th></tr>
          </thead>
          <tbody>
            {holds.map((h) => (
              <tr key={h.reference} data-testid={`control-${h.reference}`}>
                <td><code>{h.reference}</code></td>
                <td>Hold</td>
                <td>{h.amountMinor && h.currency ? formatMoney(h.amountMinor, h.currency) : '—'}</td>
                <td>{REASON_LABEL[h.reasonCode]}</td>
                <td>{titleCase(h.status)}</td>
                <td>{dateTime(h.placedAt)}</td>
                <td>{holdEnded(h)}</td>
              </tr>
            ))}
            {restrictions.map((r) => (
              <tr key={r.reference} data-testid={`control-${r.reference}`}>
                <td><code>{r.reference}</code></td>
                <td>{r.restrictionType ? RESTRICTION_LABEL[r.restrictionType] : 'Restriction'}</td>
                <td>—</td>
                <td>{REASON_LABEL[r.reasonCode]}</td>
                <td>{titleCase(r.status)}</td>
                <td>{dateTime(r.placedAt)}</td>
                <td>{r.releasedAt ? dateTime(r.releasedAt) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!isZero(account.heldMinor ?? '0') && (
        <p className="mc-admin-hint">Held funds stay in the customer's balance but cannot be used until released.</p>
      )}
    </section>
  );
}

// ── Customer ────────────────────────────────────────────────────────────────

function KycPanel({ kyc }: { kyc: KycSummary }) {
  const Icon = kyc.status === 'VERIFIED' ? ShieldCheck : kyc.status === 'REVIEW_REQUIRED' ? ShieldAlert : ShieldQuestion;
  return (
    <section className="mc-card mc-admin-panel" aria-label="KYC">
      <h2 className="mc-section-title">KYC</h2>
      <div className={`mc-admin-kyc mc-admin-kyc--${kyc.status.toLowerCase()}`} data-testid="kyc-status">
        <Icon size={18} /> {kyc.status === 'NOT_VERIFIED' ? 'Not verified' : kyc.status === 'VERIFIED' ? 'Verified' : 'Review required'}
      </div>
      {!kyc.onRecord ? (
        <p className="mc-admin-hint">No verification on record.</p>
      ) : (
        <dl className="mc-admin-dl">
          {kyc.status === 'VERIFIED' && (
            <>
              <dt>Verified</dt><dd>{dateTime(kyc.verifiedAt)}</dd>
              <dt>Provider</dt><dd>{providerName(kyc.provider)}</dd>
            </>
          )}
          {kyc.status !== 'VERIFIED' && kyc.lastVerifiedAt && (
            <>
              <dt>Last verified</dt><dd>{date(kyc.lastVerifiedAt)} ({providerName(kyc.lastVerifiedProvider)})</dd>
            </>
          )}
          {kyc.reverificationDueAt && (<><dt>Re-verification due</dt><dd>{date(kyc.reverificationDueAt)}</dd></>)}
        </dl>
      )}
      {kyc.history.length > 0 && (
        <details className="mc-admin-history">
          <summary>Verification history ({kyc.history.length})</summary>
          <ul>
            {kyc.history.map((h, i) => (
              <li key={i}>
                {titleCase(h.status)} · {providerName(h.provider)} · recorded {dateTime(h.recordedAt)}
                {h.reason ? ` · ${h.reason}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function ActivityTable({ accountNumber, onOpen }: { accountNumber: string; onOpen: (reference: string) => void }) {
  const [rows, setRows] = useState<AdminTransaction[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let alive = true;
    getActivity(accountNumber, { limit: PAGE_SIZE })
      .then((r) => { if (alive) { setRows(r.transactions); setNext(r.nextBefore); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [accountNumber]);

  const more = async () => {
    if (!next) return;
    setLoadingMore(true);
    try {
      const r = await getActivity(accountNumber, { limit: PAGE_SIZE, before: next });
      setRows((cur) => [...(cur ?? []), ...r.transactions]);
      setNext(r.nextBefore);
    } catch {
      setFailed(true);
    } finally {
      setLoadingMore(false);
    }
  };

  if (failed) return <p className="mc-admin-note" role="alert">Activity is temporarily unavailable.</p>;
  if (!rows) return <p className="mc-admin-hint">Loading activity…</p>;
  if (rows.length === 0) return <div className="mc-empty"><div className="mc-empty-title">No transactions yet</div></div>;
  return (
    <>
      <table className="mc-admin-table">
        <thead>
          <tr><th>Transaction</th><th>Date</th><th>Description</th><th>Status</th><th className="mc-admin-num">Amount</th></tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.reference}>
              <td>
                <button type="button" className="mc-link" onClick={() => onOpen(t.reference)}>{t.reference}</button>
              </td>
              <td>{dateTime(t.createdAt)}</td>
              <td>{describeTransaction(t as any)}</td>
              <td>{STATUS_LABEL[t.status]}</td>
              <td className={`mc-admin-num${t.direction === 'CREDIT' ? ' mc-tx-amount--in' : ''}`}>
                {formatSignedAmount(t.amountMinor, t.currency, t.direction)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {next && (
        <button type="button" className="mc-link mc-activity-all" onClick={more} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : 'Show older transactions'}
        </button>
      )}
    </>
  );
}

function CustomerView({ customerNumber, onBack, onOpenTransaction }: {
  customerNumber: string; onBack: () => void; onOpenTransaction: (reference: string) => void;
}) {
  const [record, setRecord] = useState<CustomerRecord | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');

  const load = useCallback(() => {
    setState('loading');
    getCustomer(customerNumber)
      .then((r) => { setRecord(r); setState('ready'); })
      .catch((err) => setState(err instanceof AdminApiError && err.status === 404 ? 'missing' : 'error'));
  }, [customerNumber]);
  useEffect(load, [load]);

  const back = <button type="button" className="mc-back" onClick={onBack}><ArrowLeft size={18} /> Search</button>;
  if (state === 'loading') return <>{back}<p className="mc-admin-hint">Loading customer…</p></>;
  if (state === 'missing') return <>{back}<div className="mc-card mc-empty"><div className="mc-empty-title">Customer not found</div></div></>;
  if (state === 'error' || !record) return <>{back}<Unavailable onRetry={load} /></>;

  const c = record.customer;
  return (
    <>
      {back}
      <h1 className="mc-page-title">{c.displayName}</h1>
      <section className="mc-card mc-admin-panel" aria-label="Customer">
        <h2 className="mc-section-title">Customer</h2>
        <dl className="mc-admin-dl">
          <dt>Customer number</dt><dd>{c.customerNumber}</dd>
          <dt>Customer type</dt><dd>{titleCase(c.customerType)}</dd>
          <dt>Display name</dt><dd>{c.displayName}</dd>
          <dt>Legal name</dt><dd>{c.legalName}</dd>
          <dt>Status</dt><dd>{titleCase(c.status)}</dd>
        </dl>
      </section>
      <KycPanel kyc={record.kyc} />
      {record.accounts.length === 0 ? (
        <section className="mc-card mc-empty"><div className="mc-empty-title">No account</div><p>This customer has no MapleCoin account.</p></section>
      ) : (
        record.accounts.map((a) => (
          <section key={a.accountNumber} className="mc-section" aria-label={`Account ${a.accountNumber}`}>
            <div className="mc-balance">
              <div className="mc-balance-label">Account {a.accountNumber} · {a.currency} · {titleCase(a.status)}</div>
              <div className="mc-balance-caption">Available to use</div>
              <div className="mc-balance-amount" data-testid={`spendable-${a.accountNumber}`}>
                {formatMoney(a.spendableMinor ?? a.availableMinor, a.currency)}
              </div>
              <dl className="mc-account-meta">
                <div><dt>Balance</dt><dd data-testid={`available-${a.accountNumber}`}>{formatMoney(a.availableMinor, a.currency)}</dd></div>
                <div><dt>On hold</dt><dd data-testid={`held-${a.accountNumber}`}>{formatMoney(a.heldMinor ?? '0', a.currency)}</dd></div>
                <div><dt>Pending</dt><dd data-testid={`pending-${a.accountNumber}`}>{formatMoney(a.pendingMinor, a.currency)}</dd></div>
                <div><dt>Opened</dt><dd>{date(a.openedAt)}</dd></div>
              </dl>
            </div>
            <ControlsPanel account={a} />
            <h2 className="mc-section-title">Activity</h2>
            <div className="mc-card"><ActivityTable accountNumber={a.accountNumber} onOpen={onOpenTransaction} /></div>
          </section>
        ))
      )}
    </>
  );
}

// ── Transaction ─────────────────────────────────────────────────────────────

function TransactionView({ reference, onBack }: { reference: string; onBack: () => void }) {
  const [t, setT] = useState<TransactionDetail | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  useEffect(() => {
    let alive = true;
    getTransaction(reference)
      .then((r) => { if (alive) { setT(r); setState('ready'); } })
      .catch((err) => { if (alive) setState(err instanceof AdminApiError && err.status === 404 ? 'missing' : 'error'); });
    return () => { alive = false; };
  }, [reference]);

  const back = <button type="button" className="mc-back" onClick={onBack}><ArrowLeft size={18} /> Customer</button>;
  if (state === 'loading') return <>{back}<p className="mc-admin-hint">Loading transaction…</p></>;
  if (state === 'missing') return <>{back}<div className="mc-card mc-empty"><div className="mc-empty-title">Transaction not found</div></div></>;
  if (state === 'error' || !t) return <>{back}<Unavailable /></>;

  return (
    <>
      {back}
      <h1 className="mc-page-title">Transaction {t.reference}</h1>
      <section className="mc-card mc-admin-panel" aria-label="Transaction">
        <dl className="mc-admin-dl">
          <dt>Description</dt><dd>{describeTransaction(t as any)}</dd>
          <dt>Type</dt><dd>{t.type}</dd>
          <dt>Direction</dt><dd>{t.direction === 'CREDIT' ? 'Incoming' : 'Outgoing'}</dd>
          <dt>Amount</dt><dd data-testid="txn-amount">{formatSignedAmount(t.amountMinor, t.currency, t.direction)}</dd>
          <dt>Status</dt><dd>{STATUS_LABEL[t.status]}</dd>
          <dt>Account</dt><dd>{t.accountNumber} ({t.customerNumber})</dd>
          <dt>Created</dt><dd>{dateTime(t.createdAt)}</dd>
          <dt>Last status change</dt><dd>{dateTime(t.statusChangedAt)}</dd>
          {t.reason && (<><dt>Reason</dt><dd>{t.reason}</dd></>)}
          {t.reversesReference && (<><dt>Reverses</dt><dd>{t.reversesReference}</dd></>)}
          {t.reversedByReference && (<><dt>Reversed by</dt><dd>{t.reversedByReference}</dd></>)}
          <dt>Source</dt><dd>{t.externalSource} · {t.externalReference}</dd>
        </dl>
      </section>
      <section className="mc-section">
        <h2 className="mc-section-title">Lifecycle</h2>
        <ol className="mc-card mc-admin-lifecycle">
          {t.events.map((e, i) => (
            <li key={i}>
              <strong>{titleCase(e.toStatus)}</strong> · {titleCase(e.action)} · {titleCase(e.actorType)} · {dateTime(e.occurredAt)}
              {e.reason ? ` · ${e.reason}` : ''}
            </li>
          ))}
        </ol>
      </section>
    </>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function ManageMapleCoin() {
  const [view, setView] = useState<View>({ kind: 'search' });
  // The last search, restored when returning from a customer.
  const [lastSearch, setLastSearch] = useState<LastSearch>({ q: '', result: null });

  let body;
  if (view.kind === 'customer') {
    body = (
      <CustomerView
        customerNumber={view.customerNumber}
        onBack={() => setView({ kind: 'search' })}
        onOpenTransaction={(reference) => setView({ kind: 'transaction', reference, customerNumber: view.customerNumber })}
      />
    );
  } else if (view.kind === 'transaction') {
    body = <TransactionView reference={view.reference} onBack={() => setView({ kind: 'customer', customerNumber: view.customerNumber })} />;
  } else {
    body = (
      <SearchView
        last={lastSearch}
        onSearched={setLastSearch}
        onOpen={(customerNumber) => setView({ kind: 'customer', customerNumber })}
      />
    );
  }

  return (
    <div className="mc mc-admin">
      <div className="mc-inner">{body}</div>
    </div>
  );
}
